import React, { useState, useEffect } from 'react';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';

echarts.use([TooltipComponent]);


type AllClientTrendsData = {
    who_is_talking: number[];
    same_word: number[];
    common_language: number[];
    open_questions: number[];
    inserted_at: string[];
};



const getDefaultOptions = (data: any, getThemeColor: (name: string) => string) => ({
    color: [
        '#e31710',  // rosso
        '#f4af36',  // giallo
        '#23a6b8',  // tiffany
        '#2e394b'   // grigio scuro
    ],
    tooltip: {
        trigger: 'axis',
        backgroundColor: getThemeColor('gray-soft'),
        borderColor: getThemeColor('gray-200'),

        axisPointer: {
            shadowStyle: {
                color: 'red'
            }
        }
    },
    legend: {
        bottom: '10',
        data: [
            {
                name: 'Who is talking',
                icon: 'roundRect'
            },
            {
                name: 'Same words',
                icon: 'roundRect'
            },
            {
                name: 'Common Language',
                icon: 'roundRect'
            },
            {
                name: 'Open questions',
                icon: 'roundRect'
            }
        ],
        itemWidth: 16,
        itemHeight: 8,
        itemGap: 10,
        inactiveColor: getThemeColor('gray-500'),
        inactiveBorderWidth: 0,
        textStyle: {
            color: getThemeColor('gray-900'),
            fontWeight: 600,
            fontSize: 16,
            fontFamily: 'Nunito Sans'
        }
    },
    xAxis: [
        {
            show: false,
            interval: 2,
            axisLine: {
                lineStyle: {
                    type: 'solid',
                    color: getThemeColor('gray-300')
                }
            },
            axisLabel: {
                color: getThemeColor('gray-900'),
                interval: 5,
                align: 'left',
                margin: 20,
                fontSize: 12.8
            },
            axisTick: {
                show: true,
                length: 15
                // alignWithLabel: true
            },
            splitLine: {
                interval: 0,
                show: true,
                lineStyle: {
                    color: getThemeColor('gray-300'),
                    type: 'dashed'
                }
            },
            type: 'category',
            boundaryGap: false,
            //data: getPastDates(15)
        },
        {
            show: false,
            interval: 2,
            axisLine: {
                show: false
            },
            axisLabel: {
                show: false
            },
            axisTick: {
                show: false
            },
            splitLine: {
                interval: 1,
                show: true,
                lineStyle: {
                    color: getThemeColor('gray-300'),
                    type: 'solid'
                }
            },
            boundaryGap: false,
            //data: getPastDates(15)
        }
    ],
    yAxis: {
        show: true,
        type: 'value',
        axisLine: {
            lineStyle: {
                type: 'solid',
                color: getThemeColor('gray-300')
            }
        },
        axisLabel: {
            color: getThemeColor('gray-900'),
            margin: 20,
            fontSize: 12.8,
            interval: 0
        },
        splitLine: {
            show: true,
            lineStyle: {
                color: getThemeColor('gray-300'),
                type: 'solid'
            }
        },
        axisTick: {
            show: true,
            length: 15,
            alignWithLabel: true,
            lineStyle: {
                color: getThemeColor('gray-300')
            }
        }
        // data: ['0', '10', '20']
    },
    series: data,
    grid: {
        right: 5,
        left: 0,
        bottom: '15%',
        top: 20,
        containLabel: true
    }
});

const AllClientsChart: React.FC<{ props: AllClientTrendsData }> = ({ props }) => {
    const { getThemeColor } = useAppContext();

    const data = [
        {
            name: 'Who is talking',
            type: 'line',
            symbolSize: 6,
            data: props.who_is_talking
        },
        {
            name: 'Same words',
            type: 'line',
            symbolSize: 6,
            data: props.same_word
        },
        {
            name: 'Common Language',
            type: 'line',
            symbolSize: 6,
            data: props.common_language
        },
        {
            name: 'Open questions',
            type: 'line',
            symbolSize: 6,
            data: props.open_questions
        }
    ];

    return (
        <ReactEChartsCore
            echarts={echarts}
            option={getDefaultOptions(data, getThemeColor)}
            style={{ minHeight: '320px', width: '100%' }}
        />
    );
};


const AllClientsTrendsMeanChart = () => {

    const auth = useAuth();
    const [data, setData] = useState<AllClientTrendsData>();

    const fetchData = async () => {
        try {
            const response = await apiClient.get(
                '/v1/myskills/allclientstrends',
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );
            const newData: AllClientTrendsData = {
                who_is_talking: [],
                same_word: [],
                common_language: [],
                open_questions: [],
                inserted_at: []
            };

            response.data.forEach((item: { who_is_talking: number; same_word: number; common_language: number; open_questions: number; inserted_at: string; }) => {
                newData.who_is_talking.push(item.who_is_talking);
                newData.same_word.push(item.same_word);
                newData.common_language.push(item.common_language);
                newData.open_questions.push(item.open_questions);
                newData.inserted_at.push(item.inserted_at);
            });
            setData(newData);
        } catch (error) {
            console.error('Request failed:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);


    return (
        <>
            {data && <AllClientsChart props={data} />}
        </>
    );


};


export default AllClientsTrendsMeanChart;
